import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ScreeningExpectedAnswer,
  ScreeningQuestionTypeEnum,
} from '@/app/common/general-types/briefs.dto';
import { genderOptions } from '@/app/common/app-config/filter-models/gender';
import { ManageBriefTabsService } from '../../../../service/manage-brief-tabs/manage-brief-tabs.service';
import {
  assignmentDraftToDto,
  assignmentDtoToDraft,
  AssignmentFilterDraft,
  emptyAssignmentFilterDraft,
  parseScreeningChipKey,
  screeningChipKey,
  screeningFilterValueLabel,
  screeningQuestionFilterOptions,
  truncateQuestionLabel,
} from './assignment-filters.helpers';

export type AssignmentFiltersMode = 'trigger' | 'chips';

export interface AssignmentCountryOption {
  id: string;
  name: string;
}

export interface AssignmentFilterChip {
  key: AssignmentFilterChipKey;
  label: string;
}

export type AssignmentFilterChipKey =
  | 'country'
  | 'gender'
  | 'rating'
  | 'age'
  | 'isVerified'
  | 'hasReviews'
  | 'tikTokFollowersFrom'
  | 'instagramFollowersFrom'
  | `screening:${string}`;

export const ASSIGNMENT_COUNTRY_OPTIONS: AssignmentCountryOption[] = [
  { id: 'GB', name: 'UK' },
  { id: 'US', name: 'USA' },
  { id: 'IE', name: 'Ireland' },
];

export interface AssignmentRatingOption {
  value: number;
  label: string;
}

export const ASSIGNMENT_RATING_OPTIONS: AssignmentRatingOption[] = [
  { value: 3, label: '3+' },
  { value: 4, label: '4+' },
  { value: 4.5, label: '4.5+' },
];

export interface AssignmentScreeningQuestionView {
  id: string;
  question: string;
  type: ScreeningQuestionTypeEnum;
  expected?: ScreeningExpectedAnswer;
  options: { value: string; label: string }[];
}

@Component({
  selector: 'mbt-assignment-filters',
  templateUrl: './assignment-filters.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'contents',
  },
})
export class AssignmentFilters {
  @ViewChild('assignmentFiltersDialog')
  assignmentFiltersDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('assignmentFiltersButton')
  assignmentFiltersButton?: ElementRef<HTMLButtonElement>;

  readonly mode = input<AssignmentFiltersMode>('trigger');

  readonly manageBriefTabsService = inject(ManageBriefTabsService);

  readonly draft = signal<AssignmentFilterDraft>(emptyAssignmentFilterDraft());
  readonly panelTop = signal(0);
  readonly panelLeft = signal(0);

  readonly countryOptions = ASSIGNMENT_COUNTRY_OPTIONS;
  readonly ratingOptions = ASSIGNMENT_RATING_OPTIONS;
  readonly genderOptions = genderOptions;

  readonly isSocialBrief = computed(
    () => !!this.manageBriefTabsService.focussedBrief()?.shoutBrief,
  );

  readonly screeningQuestions = computed((): AssignmentScreeningQuestionView[] => {
    const qs = this.manageBriefTabsService.focussedBrief()?.screeningQuestions ?? [];
    return qs.map((q, index) => {
      const hasScorable = !!q.expected && q.expected.kind !== 'manual';
      return {
        // Prefer stable question ids; fall back to index for older briefs.
        id: q.id || `idx:${index}`,
        question: q.question,
        type: q.type,
        expected: q.expected,
        options: screeningQuestionFilterOptions(q.type, hasScorable),
      };
    });
  });

  readonly appliedFilters = computed(() => this.manageBriefTabsService.applicantProfileFilters());
  readonly appliedScreeningFilters = computed(
    () => this.manageBriefTabsService.applicantScreeningFilters(),
  );

  readonly activeChipCount = computed(() => this.activeChips().length);

  readonly activeChips = computed((): AssignmentFilterChip[] => {
    const filters = this.appliedFilters();
    const screening = this.appliedScreeningFilters();
    const chips: AssignmentFilterChip[] = [];

    if (filters.country?.length) {
      const code = filters.country[0];
      const name =
        ASSIGNMENT_COUNTRY_OPTIONS.find((c) => c.id === code)?.name ?? code;
      chips.push({ key: 'country', label: `Country: ${name}` });
    }

    if (filters.gender !== undefined && filters.gender !== null) {
      const genderId = Array.isArray(filters.gender)
        ? filters.gender[0]
        : Number(filters.gender);
      const name =
        genderOptions.find((g) => g.id === genderId)?.name ?? String(genderId);
      chips.push({ key: 'gender', label: `Gender: ${name}` });
    }

    if (filters.rating != null) {
      const ratingLabel =
        ASSIGNMENT_RATING_OPTIONS.find((o) => o.value === filters.rating)?.label
        ?? `${filters.rating}+`;
      chips.push({ key: 'rating', label: `Min rating: ${ratingLabel}` });
    }

    if (filters.ageFrom != null || filters.ageTo != null) {
      const from = filters.ageFrom ?? '…';
      const to = filters.ageTo ?? '…';
      chips.push({ key: 'age', label: `Age: ${from}–${to}` });
    }

    if (filters.isVerified === true) {
      chips.push({ key: 'isVerified', label: 'Verified only' });
    }

    if (filters.hasReviews === true) {
      chips.push({ key: 'hasReviews', label: 'Has reviews' });
    }

    if (filters.tikTokFollowersFrom != null) {
      chips.push({
        key: 'tikTokFollowersFrom',
        label: `Min TikTok: ${filters.tikTokFollowersFrom.toLocaleString('en-GB')}`,
      });
    }

    if (filters.instagramFollowersFrom != null) {
      chips.push({
        key: 'instagramFollowersFrom',
        label: `Min Instagram: ${filters.instagramFollowersFrom.toLocaleString('en-GB')}`,
      });
    }

    const questions = this.screeningQuestions();
    for (const [questionId, value] of Object.entries(screening)) {
      if (!value) {
        continue;
      }
      const question = questions.find((q) => q.id === questionId)?.question ?? 'Screening';
      chips.push({
        key: screeningChipKey(questionId),
        label: `${truncateQuestionLabel(question, 28)}: ${screeningFilterValueLabel(value)}`,
      });
    }

    return chips;
  });

  openPanel(): void {
    this.draft.set(
      assignmentDtoToDraft(this.appliedFilters(), this.appliedScreeningFilters()),
    );
    this.syncPanelPosition();
  }

  private syncPanelPosition(): void {
    const button = this.assignmentFiltersButton?.nativeElement;
    if (!button) {
      return;
    }
    const rect = button.getBoundingClientRect();
    this.panelTop.set(rect.bottom);
    this.panelLeft.set(rect.left);
  }

  closePanel(): void {
    this.assignmentFiltersDialog?.nativeElement?.close();
  }

  applyFilters(): void {
    const current = this.draft();
    const dto = assignmentDraftToDto(current, this.isSocialBrief());
    this.manageBriefTabsService.applicantProfileFilters.set(dto);
    this.manageBriefTabsService.applicantScreeningFilters.set({ ...current.screening });
    this.manageBriefTabsService.refreshApplicants();
    this.closePanel();
  }

  clearFilters(): void {
    this.draft.set(emptyAssignmentFilterDraft());
    this.manageBriefTabsService.clearApplicantProfileFilters();
    this.manageBriefTabsService.refreshApplicants();
    this.closePanel();
  }

  removeChip(key: AssignmentFilterChipKey): void {
    const screeningQuestionId = parseScreeningChipKey(key);
    if (screeningQuestionId) {
      const next = { ...this.appliedScreeningFilters() };
      delete next[screeningQuestionId];
      this.manageBriefTabsService.applicantScreeningFilters.set(next);
      this.manageBriefTabsService.refreshApplicants();
      return;
    }

    const current = { ...this.appliedFilters() };

    switch (key) {
      case 'country':
        delete current.country;
        break;
      case 'gender':
        delete current.gender;
        break;
      case 'rating':
        delete current.rating;
        break;
      case 'age':
        delete current.ageFrom;
        delete current.ageTo;
        break;
      case 'isVerified':
        delete current.isVerified;
        break;
      case 'hasReviews':
        delete current.hasReviews;
        break;
      case 'tikTokFollowersFrom':
        delete current.tikTokFollowersFrom;
        break;
      case 'instagramFollowersFrom':
        delete current.instagramFollowersFrom;
        break;
    }

    this.manageBriefTabsService.applicantProfileFilters.set(current);
    this.manageBriefTabsService.refreshApplicants();
  }

  selectCountry(countryId: string | null): void {
    this.draft.update((d) => ({ ...d, country: countryId }));
  }

  selectGender(genderId: number | null): void {
    this.draft.update((d) => ({ ...d, gender: genderId }));
  }

  selectRating(rating: number | null): void {
    this.draft.update((d) => ({ ...d, rating }));
  }

  selectScreening(questionId: string, value: string | null): void {
    this.draft.update((d) => {
      const screening = { ...d.screening };
      if (!value) {
        delete screening[questionId];
      } else {
        screening[questionId] = value;
      }
      return { ...d, screening };
    });
  }

  onAgeFromInput(value: string): void {
    this.draft.update((d) => ({ ...d, ageFrom: this.parseOptionalNumber(value) }));
  }

  onAgeToInput(value: string): void {
    this.draft.update((d) => ({ ...d, ageTo: this.parseOptionalNumber(value) }));
  }

  onTikTokMinInput(value: string): void {
    this.draft.update((d) => ({
      ...d,
      tikTokFollowersFrom: this.parseGroupedInteger(value),
    }));
  }

  onInstagramMinInput(value: string): void {
    this.draft.update((d) => ({
      ...d,
      instagramFollowersFrom: this.parseGroupedInteger(value),
    }));
  }

  formatFollowerCount(value: number | null | undefined): string {
    if (value == null) {
      return '';
    }
    return value.toLocaleString('en-GB');
  }

  setVerifiedOnly(checked: boolean): void {
    this.draft.update((d) => ({ ...d, isVerified: checked }));
  }

  setHasReviews(checked: boolean): void {
    this.draft.update((d) => ({ ...d, hasReviews: checked }));
  }

  private parseOptionalNumber(value: string): number | null {
    const trimmed = value?.trim() ?? '';
    if (!trimmed) {
      return null;
    }
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  }

  private parseGroupedInteger(value: string): number | null {
    const digits = (value ?? '').replace(/[^\d]/g, '');
    if (!digits) {
      return null;
    }
    const num = Number(digits);
    return Number.isFinite(num) ? num : null;
  }
}
